import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import CollegeCard from '../components/common/CollegeCard';
import CallToAction from '../components/common/CallToAction';
import { MapPin, ExternalLink, Search } from 'lucide-react';

// Featured colleges
const featuredColleges = [
  {
    id: 'sourabh',
    name: 'Sourabh College of Veterinary Science',
    location: 'Hindaun City, Rajasthan',
    imageUrl: 'https://images.pexels.com/photos/1472462/pexels-photo-1472462.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    description: 'A premier institute offering quality education in veterinary science with state-of-the-art facilities and experienced faculty.'
  },
  {
    id: 'rps',
    name: 'RPS College of Veterinary Science',
    location: 'Mahendergarh, Haryana',
    imageUrl: 'https://images.pexels.com/photos/3786157/pexels-photo-3786157.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    description: 'Renowned for its excellent academic programs and cutting-edge research facilities in animal health and welfare.'
  },
  {
    id: 'abhilashi',
    name: 'Abhilashi Veterinary College',
    location: 'Mandi, Himachal Pradesh',
    imageUrl: 'https://images.pexels.com/photos/267885/pexels-photo-267885.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    description: 'Known for its holistic approach to veterinary education with focus on practical training and modern teaching methodologies.'
  }
];

// All private veterinary colleges in India
const privateColleges = [
  { name: 'Sourabh College of Veterinary Science', location: 'Hindaun City, Rajasthan', fees: '₹4.5 Lakhs/year', seats: 100 },
  { name: 'RPS College of Veterinary Science', location: 'Mahendergarh, Haryana', fees: '₹3.8 Lakhs/year', seats: 80 },
  { name: 'Abhilashi Veterinary College', location: 'Mandi, Himachal Pradesh', fees: '₹3.2 Lakhs/year', seats: 60 },
  { name: 'Apollo College of Veterinary Medicine', location: 'Jaipur, Rajasthan', fees: '₹4.2 Lakhs/year', seats: 100 },
  { name: 'Arawali Veterinary College', location: 'Sikar, Rajasthan', fees: '₹3.9 Lakhs/year', seats: 80 },
  { name: 'Dolphin Institute of Veterinary Sciences', location: 'Dehradun, Uttarakhand', fees: '₹3.5 Lakhs/year', seats: 60 },
  { name: 'IIVER College of Veterinary Sciences', location: 'Rohtak, Haryana', fees: '₹4.0 Lakhs/year', seats: 100 },
  { name: 'Krantisinh Nana Patil College of Veterinary Science', location: 'Satara, Maharashtra', fees: '₹3.7 Lakhs/year', seats: 80 },
  { name: 'Mahatma Gandhi Veterinary College', location: 'Bharatpur, Rajasthan', fees: '₹4.1 Lakhs/year', seats: 100 },
  { name: 'Parul Institute of Veterinary Science', location: 'Vadodara, Gujarat', fees: '₹4.3 Lakhs/year', seats: 120 },
  { name: 'RVDEC College of Veterinary Science', location: 'Jaipur, Rajasthan', fees: '₹3.8 Lakhs/year', seats: 80 },
  { name: 'Sri Venkateswara Veterinary University', location: 'Tirupati, Andhra Pradesh', fees: '₹3.0 Lakhs/year', seats: 60 },
  { name: 'William Carey University College of Veterinary Sciences', location: 'Shillong, Meghalaya', fees: '₹3.5 Lakhs/year', seats: 60 },
  { name: 'Vaishnavi College of Veterinary Science', location: 'Meerut, Uttar Pradesh', fees: '₹3.9 Lakhs/year', seats: 80 },
  { name: 'Shri Krishna College of Veterinary Science', location: 'Patna, Bihar', fees: '₹3.6 Lakhs/year', seats: 60 }
];

const CollegeListingPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Update document title
    document.title = 'Veterinary Colleges - VetEdu Consultants';
  }, []);

  // Filter colleges based on search term
  const filteredColleges = privateColleges.filter(college => 
    college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    college.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Banner 
        title="Veterinary Colleges in India"
        subtitle="Explore top BVSc colleges and find the perfect fit for your veterinary education"
        backgroundImage="https://images.pexels.com/photos/3861458/pexels-photo-3861458.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Featured Colleges" 
            subtitle="Our partner institutions offering excellent veterinary education and training."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            {featuredColleges.map(college => (
              <CollegeCard 
                key={college.id}
                id={college.id}
                name={college.name}
                location={college.location}
                imageUrl={college.imageUrl}
                description={college.description}
              />
            ))}
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Private Veterinary Colleges in India" 
            subtitle="A comprehensive list of private institutions offering BVSc programs."
          />
          
          <div className="mb-8 max-w-lg mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by college name or location..."
                className="form-input pl-12"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      College Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Annual Fees
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Available Seats
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredColleges.map((college, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{college.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin size={16} className="mr-1 text-primary-500" />
                          {college.location}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{college.fees}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{college.seats}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = `/contact?college=${encodeURIComponent(college.name)}`;
                          }}
                          className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-800"
                        >
                          Apply Now <ExternalLink size={14} className="ml-1" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredColleges.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                No colleges found matching your search criteria.
              </div>
            )}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Need assistance with admissions to any of these colleges?
            </p>
            <Link to="/contact" className="btn btn-primary">
              Contact Our Counselors
            </Link>
          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Get Expert Guidance for College Selection"
        description="Our counselors can help you choose the right college based on your preferences, budget, and career goals."
        buttonText="Book a Counseling Session"
        buttonLink="/contact"
      />
    </>
  );
};

export default CollegeListingPage;